import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  UserPlus, UserMinus, Users, Plus, Search, Eye, Trash2, Play,
  FileText, CheckCircle, Clock, AlertTriangle, Send, Loader2,
  ClipboardList, BarChart3, Shield
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HRWorkflowsPage = () => {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [employees, setEmployees] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isQuestionnaireOpen, setIsQuestionnaireOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [questionnaire, setQuestionnaire] = useState(null);
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [report, setReport] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', department: '', role: '', start_date: '', termination_date: '', status: 'active' });

  useEffect(() => { fetchDashboard(); fetchEmployees(); }, [search, statusFilter]);

  const fetchDashboard = async () => {
    try {
      const res = await axios.get(`${API}/hr/dashboard`, { headers: { Authorization: `Bearer ${token}` } });
      setDashboard(res.data);
    } catch { /* silent */ }
  };

  const fetchEmployees = async () => {
    try {
      const params = {};
      if (search) params.search = search;
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API}/hr/employees`, { params, headers: { Authorization: `Bearer ${token}` } });
      setEmployees(res.data);
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!form.name || !form.email) { toast.error('Name and email required'); return; }
    try {
      await axios.post(`${API}/hr/employees`, form, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Employee added');
      setIsCreateOpen(false);
      setForm({ name: '', email: '', department: '', role: '', start_date: '', termination_date: '', status: 'active' });
      fetchEmployees(); fetchDashboard();
    } catch { toast.error('Failed to create'); }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this employee record?')) return;
    try {
      await axios.delete(`${API}/hr/employees/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Deleted'); fetchEmployees(); fetchDashboard();
    } catch { toast.error('Failed'); }
  };

  const startOffboarding = async (emp) => {
    try {
      const res = await axios.post(`${API}/offboarding/start`, { employee_id: emp.employee_id }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Offboarding started for ${emp.name}`);
      setQuestionnaire({ type: 'offboarding', questions: res.data.questions, employee: emp });
      setAnswers({});
      setIsQuestionnaireOpen(true);
      fetchEmployees(); fetchDashboard();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const startOnboardingCheckin = async (emp, phase) => {
    const templates = { week_1: 'onboarding_week1', month_1: 'onboarding_month1', day_90: 'onboarding_day90' };
    try {
      const tplRes = await axios.get(`${API}/hr/questionnaires/templates`, { headers: { Authorization: `Bearer ${token}` } });
      const questions = tplRes.data.onboarding[phase] || [];
      setQuestionnaire({ type: templates[phase], questions, employee: emp, phase });
      setAnswers({});
      setIsQuestionnaireOpen(true);
    } catch { toast.error('Failed to load questionnaire'); }
  };

  const submitQuestionnaire = async () => {
    if (!questionnaire) return;
    const responses = questionnaire.questions.map(q => ({
      question_id: q.id, answer: answers[q.id] || '', category: q.category,
    })).filter(r => r.answer);

    if (responses.length === 0) { toast.error('Please answer at least one question'); return; }
    setSubmitting(true);
    try {
      const endpoint = questionnaire.type === 'offboarding' ? '/offboarding/respond' : '/onboarding/checkin';
      const res = await axios.post(`${API}${endpoint}`, {
        employee_id: questionnaire.employee.employee_id,
        flow_type: questionnaire.type,
        responses,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Responses submitted — ${res.data.routed_items?.length || 0} items routed`);
      setIsQuestionnaireOpen(false);
      fetchEmployees(); fetchDashboard();
    } catch { toast.error('Failed to submit'); }
    finally { setSubmitting(false); }
  };

  const viewEmployee = async (emp) => {
    try {
      const res = await axios.get(`${API}/hr/employees/${emp.employee_id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelected(res.data);
      // Get report if offboarding
      if (res.data.status === 'offboarding' || res.data.status === 'departed') {
        try {
          const rpt = await axios.get(`${API}/offboarding/report/${emp.employee_id}`, { headers: { Authorization: `Bearer ${token}` } });
          setReport(rpt.data);
        } catch { setReport(null); }
      } else { setReport(null); }
      setIsViewOpen(true);
    } catch { toast.error('Failed'); }
  };

  const getStatusBadge = (s) => {
    switch (s) {
      case 'onboarding': return 'bg-[#007AFF] text-white';
      case 'offboarding': return 'bg-[#FF9500] text-white';
      case 'departed': return 'bg-[#8E8E93] text-white';
      default: return 'bg-[#34C759] text-white';
    }
  };

  return (
    <div data-testid="hr-workflows-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A]">HR Workflows</h1>
          <p className="text-[#4B5563] text-sm">Onboarding & offboarding automation</p>
        </div>
        <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={() => setIsCreateOpen(true)} data-testid="add-employee-btn">
          <Plus className="w-4 h-4 mr-2" />Add Employee
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="dashboard" data-testid="hr-tab-dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="employees" data-testid="hr-tab-employees">Employees</TabsTrigger>
          <TabsTrigger value="onboarding" data-testid="hr-tab-onboarding">Onboarding</TabsTrigger>
          <TabsTrigger value="offboarding" data-testid="hr-tab-offboarding">Offboarding</TabsTrigger>
        </TabsList>

        {/* Dashboard */}
        <TabsContent value="dashboard">
          {dashboard && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="hr-stats">
                {[
                  { label: 'Total', value: dashboard.total_employees, icon: Users, color: 'text-[#002FA7]' },
                  { label: 'Active', value: dashboard.by_status?.active || 0, icon: CheckCircle, color: 'text-[#34C759]' },
                  { label: 'Onboarding', value: dashboard.by_status?.onboarding || 0, icon: UserPlus, color: 'text-[#007AFF]' },
                  { label: 'Offboarding', value: dashboard.by_status?.offboarding || 0, icon: UserMinus, color: 'text-[#FF9500]' },
                  { label: 'Pending Q', value: dashboard.questionnaires?.pending || 0, icon: ClipboardList, color: 'text-[#FFCC00]' },
                ].map((s) => {
                  const Icon = s.icon;
                  return (
                    <Card key={s.label} className="border-[#E5E7EB]">
                      <CardContent className="p-4 text-center">
                        <Icon className={`w-5 h-5 mx-auto mb-1 ${s.color}`} />
                        <p className="text-2xl font-bold">{s.value}</p>
                        <p className="text-xs text-[#9CA3AF]">{s.label}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
              {dashboard.recent_employees?.length > 0 && (
                <Card className="border-[#E5E7EB]">
                  <CardHeader><CardTitle className="text-sm">Recent Employees</CardTitle></CardHeader>
                  <CardContent>
                    {dashboard.recent_employees.map(e => (
                      <div key={e.employee_id} className="flex items-center justify-between py-2 border-b border-[#F3F4F6] last:border-0">
                        <span className="text-sm">{e.name}</span>
                        <Badge className={getStatusBadge(e.status)}>{e.status}</Badge>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* Employees */}
        <TabsContent value="employees">
          <Card className="border-[#E5E7EB] mb-4">
            <CardContent className="p-4 flex gap-3">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="emp-search" />
              </div>
              <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
                <SelectTrigger className="w-[150px]"><SelectValue placeholder="Status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="onboarding">Onboarding</SelectItem>
                  <SelectItem value="offboarding">Offboarding</SelectItem>
                  <SelectItem value="departed">Departed</SelectItem>
                </SelectContent>
              </Select>
            </CardContent>
          </Card>
          <Card className="border-[#E5E7EB]">
            <CardContent className="p-0">
              <Table data-testid="employees-table">
                <TableHeader><TableRow className="bg-[#F3F4F6]">
                  <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Dept</TableHead><TableHead>Role</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Actions</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {employees.map(e => (
                    <TableRow key={e.employee_id}>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-[#9CA3AF]">{e.email}</TableCell>
                      <TableCell>{e.department}</TableCell>
                      <TableCell>{e.role}</TableCell>
                      <TableCell><Badge className={getStatusBadge(e.status)}>{e.status}</Badge></TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="icon" onClick={() => viewEmployee(e)} data-testid={`view-emp-${e.employee_id}`}><Eye className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" className="text-[#FF3B30]" onClick={() => handleDelete(e.employee_id)}><Trash2 className="w-4 h-4" /></Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Onboarding */}
        <TabsContent value="onboarding">
          <div className="space-y-3">
            {employees.filter(e => e.status === 'onboarding' || e.status === 'active').map(e => (
              <Card key={e.employee_id} className="border-[#E5E7EB]" data-testid={`onboard-card-${e.employee_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{e.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{e.department} · {e.role} · Started: {e.start_date || 'N/A'}</p>
                    {e.onboarding_score !== null && e.onboarding_score !== undefined && (
                      <Badge className={e.onboarding_score >= 70 ? 'bg-[#34C759] text-white mt-1' : 'bg-[#FF9500] text-white mt-1'}>Score: {e.onboarding_score}</Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button size="sm" variant="outline" onClick={() => startOnboardingCheckin(e, 'week_1')} data-testid={`checkin-w1-${e.employee_id}`}>Week 1</Button>
                    <Button size="sm" variant="outline" onClick={() => startOnboardingCheckin(e, 'month_1')}>Month 1</Button>
                    <Button size="sm" variant="outline" onClick={() => startOnboardingCheckin(e, 'day_90')}>Day 90</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
            {employees.filter(e => e.status === 'onboarding' || e.status === 'active').length === 0 && (
              <Card className="border-[#E5E7EB]"><CardContent className="text-center py-12 text-[#9CA3AF]"><UserPlus className="w-10 h-10 mx-auto mb-3 text-[#E5E7EB]" /><p>No onboarding employees</p></CardContent></Card>
            )}
          </div>
        </TabsContent>

        {/* Offboarding */}
        <TabsContent value="offboarding">
          <div className="space-y-3">
            {employees.filter(e => e.status === 'offboarding' || e.status === 'active').map(e => (
              <Card key={e.employee_id} className="border-[#E5E7EB]" data-testid={`offboard-card-${e.employee_id}`}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-medium">{e.name}</p>
                    <p className="text-xs text-[#9CA3AF]">{e.department} · {e.role} · Leaving: {e.termination_date || 'N/A'}</p>
                  </div>
                  <div className="flex gap-1">
                    {e.status !== 'offboarding' && (
                      <Button size="sm" className="bg-[#FF9500] hover:bg-[#E08600]" onClick={() => startOffboarding(e)} data-testid={`start-offboard-${e.employee_id}`}>
                        <UserMinus className="w-3 h-3 mr-1" />Start Exit
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => viewEmployee(e)}><Eye className="w-3 h-3 mr-1" />View</Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Create Employee Dialog */}
      <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
        <DialogContent data-testid="create-employee-dialog">
          <DialogHeader><DialogTitle>Add Employee</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name *</Label><Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} data-testid="emp-name-input" /></div>
              <div><Label>Email *</Label><Input type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} data-testid="emp-email-input" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Department</Label><Input value={form.department} onChange={e => setForm({...form, department: e.target.value})} /></div>
              <div><Label>Role</Label><Input value={form.role} onChange={e => setForm({...form, role: e.target.value})} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={e => setForm({...form, start_date: e.target.value})} /></div>
              <div><Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({...form, status: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="onboarding">Onboarding</SelectItem>
                    <SelectItem value="offboarding">Offboarding</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={handleCreate} data-testid="save-employee-btn">Add Employee</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Employee Dialog */}
      <Dialog open={isViewOpen} onOpenChange={setIsViewOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="view-employee-dialog">
          <DialogHeader><DialogTitle>{selected?.name}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="flex gap-2">
                <Badge className={getStatusBadge(selected.status)}>{selected.status}</Badge>
                {selected.onboarding_score !== null && selected.onboarding_score !== undefined && (
                  <Badge className={selected.onboarding_score >= 70 ? 'bg-[#34C759] text-white' : 'bg-[#FF9500] text-white'}>Onboarding Score: {selected.onboarding_score}</Badge>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div><span className="text-[#9CA3AF]">Email</span><p>{selected.email}</p></div>
                <div><span className="text-[#9CA3AF]">Department</span><p>{selected.department || 'N/A'}</p></div>
                <div><span className="text-[#9CA3AF]">Role</span><p>{selected.role || 'N/A'}</p></div>
                <div><span className="text-[#9CA3AF]">Start Date</span><p>{selected.start_date || 'N/A'}</p></div>
              </div>
              {/* Routed Items */}
              {selected.routed_items?.length > 0 && (
                <div>
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><BarChart3 className="w-4 h-4" />Routed Items ({selected.routed_items.length})</h4>
                  <div className="space-y-1">
                    {selected.routed_items.map((r, i) => (
                      <div key={i} className="p-2 bg-[#F3F4F6] rounded text-xs">
                        <div className="flex justify-between">
                          <Badge variant="secondary">{r.category}</Badge>
                          <span className="text-[#9CA3AF]">{r.actions_taken?.map(a => a.type).join(', ')}</span>
                        </div>
                        <p className="mt-1 text-[#4B5563] truncate">{r.answer_preview}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Offboarding Report */}
              {report && (
                <div className="border-t border-[#E5E7EB] pt-4">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><FileText className="w-4 h-4" />Offboarding Report</h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="p-2 bg-[#F3F4F6] rounded"><span className="text-[#9CA3AF]">Questionnaire</span><p className="font-medium">{report.questionnaire_status}</p></div>
                    <div className="p-2 bg-[#F3F4F6] rounded"><span className="text-[#9CA3AF]">KT Tasks</span><p className="font-medium">{report.knowledge_transfer?.tasks_created} ({report.knowledge_transfer?.all_complete ? 'Complete' : 'In Progress'})</p></div>
                    <div className="p-2 bg-[#F3F4F6] rounded"><span className="text-[#9CA3AF]">Risk Flags</span><p className={`font-medium ${report.risk_flags?.count > 0 ? 'text-[#FF3B30]' : ''}`}>{report.risk_flags?.count}</p></div>
                    <div className="p-2 bg-[#F3F4F6] rounded"><span className="text-[#9CA3AF]">Process Items</span><p className="font-medium">{report.process_improvements?.count}</p></div>
                  </div>
                </div>
              )}
              {/* Audit Trail */}
              {selected.audit_trail?.length > 0 && (
                <div className="border-t border-[#E5E7EB] pt-4">
                  <h4 className="font-medium text-sm mb-2 flex items-center gap-2"><Shield className="w-4 h-4" />Audit Trail</h4>
                  <div className="space-y-1">
                    {selected.audit_trail.map((a, i) => (
                      <div key={i} className="flex justify-between text-xs py-1 border-b border-[#F3F4F6]">
                        <span>{a.action}</span>
                        <span className="text-[#9CA3AF]">{a.by} · {a.at ? format(new Date(a.at), 'MMM d, h:mm a') : ''}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Questionnaire Dialog */}
      <Dialog open={isQuestionnaireOpen} onOpenChange={setIsQuestionnaireOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="questionnaire-dialog">
          <DialogHeader>
            <DialogTitle>{questionnaire?.type === 'offboarding' ? 'Exit Interview' : `Onboarding Check-in: ${questionnaire?.phase?.replace('_', ' ')}`}</DialogTitle>
          </DialogHeader>
          {questionnaire && (
            <div className="space-y-4">
              <p className="text-sm text-[#4B5563]">Employee: <strong>{questionnaire.employee?.name}</strong></p>
              {questionnaire.questions.map((q) => (
                <div key={q.id} className="p-3 border border-[#E5E7EB] rounded-lg">
                  <Label className="text-sm font-medium">{q.text}</Label>
                  <Badge variant="secondary" className="text-[10px] ml-2">{q.category.replace('_', ' ')}</Badge>
                  <Textarea
                    className="mt-2"
                    rows={3}
                    value={answers[q.id] || ''}
                    onChange={e => setAnswers({ ...answers, [q.id]: e.target.value })}
                    placeholder="Enter response..."
                    data-testid={`answer-${q.id}`}
                  />
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsQuestionnaireOpen(false)}>Cancel</Button>
            <Button className="bg-[#002FA7] hover:bg-[#00227A]" onClick={submitQuestionnaire} disabled={submitting} data-testid="submit-questionnaire-btn">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
              Submit Responses
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HRWorkflowsPage;
